import React, { useState, useEffect } from 'react';
import NavBar from '../components/NavBar';
import { Box, Typography, CircularProgress, Select, MenuItem, FormControl, InputLabel, Button } from '@mui/material';
import { useSidebar } from '../contexts/SidebarContext';
import axios from 'axios';
import { supabase } from '../supabaseClient';
import { API_URL } from '../globals';
import { Terminal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Pages from './Pages';

// TestPages component that wraps Pages without NavBar
const TestPages = ({ studioId }) => {
  return (
    <Box sx={{ 
      mt: -2, // Offset the padding from the main Test component
      mx: -2, // Remove horizontal padding to align with header
      minHeight: 'calc(100vh - 120px)', // Adjust height to account for Test header
    }}>
      <Pages hideNavBar={true} studioId={studioId} />
    </Box>
  );
};

function Test() {
  const { isOpen, isHovered } = useSidebar();
  const navbarWidth = isOpen || isHovered ? 240 : 64;
  const navigate = useNavigate();
  
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStudios = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error("Not authenticated");
      }
      const authToken = session.data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/studio/seeds`, { headers });
      
      if (response.data) {
        const sortedStudios = [...response.data].sort((a, b) => 
          a.display_name.localeCompare(b.display_name)
        );
        setStudios(sortedStudios);
      } else {
        setStudios([]);
      }
    } catch (err) {
      console.error("Error fetching studios:", err);
      setError(err.message || "Failed to fetch studios");
      setStudios([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStudios();
  }, []);

  useEffect(() => {
    if (studios.length > 0) {
      const savedStudio = localStorage.getItem('selectedStudio');
      const savedStudioId = savedStudio ? parseInt(savedStudio, 10) : null;
      const savedStudioExists = savedStudioId && studios.find(studio => studio.id === savedStudioId);
      
      if (savedStudioExists) {
        setSelectedStudio(savedStudioId);
      }
    }
  }, [studios]);


  return (
    <>
      <NavBar />
      
      <Box
        component="main" 
        sx={{
          minHeight: '100vh', 
          marginLeft: `${navbarWidth}px`,
          transition: 'margin-left 0.2s',
          width: `calc(100% - ${navbarWidth}px)`,
          p: 3,
          backgroundColor: '#121212',
          boxSizing: 'border-box'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#fff' }}>
            Tests
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedStudio && (
              <Button
                variant="contained"
                onClick={() => navigate(`/create-tests/${selectedStudio}`)}
                sx={{
                  backgroundColor: '#fff',
                  color: '#121212',
                  borderRadius: '8px',
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  },
                }}
              >
                + Add Tests
              </Button>
            )}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel sx={{ color: "#888", "&.Mui-focused": { color: "#fff" } }}>
                Select Studio
              </InputLabel>
              <Select
                value={selectedStudio}
                onChange={(e) => {
                  const newStudio = e.target.value;
                  setSelectedStudio(newStudio);
                  localStorage.setItem('selectedStudio', newStudio);
                }}
                label="Select Studio"
                sx={{
                  backgroundColor: "#1a1a1a",
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2a2a2a" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#4a90e2" },
                  "& .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                {studios.map((studio) => (
                  <MenuItem key={studio.id} value={studio.id}>
                    {studio.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress sx={{ color: '#fff' }} />
          </Box>
        )}

        {error && (
          <Typography sx={{ color: 'error.main', textAlign: 'center', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Error loading studios: {error}
          </Typography>
        )}

        {!isLoading && !error && (
          <Box sx={{ width: '100%' }}>
            {studios.length > 0 ? (
              selectedStudio ? (
                <TestPages studioId={selectedStudio} />
              ) : (
                <Box sx={{
                  p: 6,
                  textAlign: 'center',
                  border: '1px dashed #333',
                  borderRadius: 1,
                  backgroundColor: '#1a1a1a',
                  minHeight: '300px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Terminal size={32} style={{ color: '#444', marginBottom: '16px' }} />
                  <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
                    Please select a studio from the dropdown above to view tests.
                  </Typography>
                </Box>
              )
            ) : (
              <Box sx={{
                p: 6,
                textAlign: 'center',
                border: '1px dashed #333',
                borderRadius: 1,
                backgroundColor: '#1a1a1a',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Terminal size={32} style={{ color: '#444', marginBottom: '16px' }} />
                <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
                  No studios found. Create a studio first to run tests.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  );
}

export default Test;
